import { APICategory, UnreleasedAPICategory } from "../models/Entities";
import useAppContext from "../components/context/useAppContext";
import { ADDITIONAL_COMMON_MODEL_INFO, DeprecationStage } from "../constants";
import { resolveDeprecationMessage } from "../components/pages/configuration/common-models/common-model-scopes/utils/resolveDeprecationMessage";
import { useCallback } from "react";

type UseDeprecatedCommonModelProps = {
  category: APICategory | UnreleasedAPICategory;
  commonModel?: string;
  field?: string;
};

const useDeprecatedCommonModel = ({ category, commonModel }: UseDeprecatedCommonModelProps) => {
  // hooks
  const { showDeprecatedSunsetItems } = useAppContext();

  // helpers
  const getAdditionalCommonModelInfo = useCallback(
    (commonModel?: string) => {
      const noInfo = {
        deprecationStage: undefined,
        deprecationMessage: undefined,
        fields: undefined,
      };
      const additionalCommonModelInfo = ADDITIONAL_COMMON_MODEL_INFO[category];

      if (!additionalCommonModelInfo) return noInfo;

      return commonModel && commonModel in additionalCommonModelInfo
        ? additionalCommonModelInfo[commonModel]
        : noInfo;
    },
    [category],
  );

  const filterDeprecatedFields = useCallback(
    (commonModel: string, fields: string[]): string[] => {
      const { fields: deprecatedFields } = getAdditionalCommonModelInfo(commonModel);

      return fields.filter(
        (field) =>
          !deprecatedFields ||
          !(field in deprecatedFields) ||
          deprecatedFields[field].deprecationStage !== DeprecationStage.SUNSET ||
          showDeprecatedSunsetItems,
      );
    },
    [getAdditionalCommonModelInfo, showDeprecatedSunsetItems],
  );

  const filterDeprecatedCommonModels = useCallback(
    (commonModels: string[]) => {
      return commonModels.filter((commonModel) => {
        const { deprecationStage } = getAdditionalCommonModelInfo(commonModel);

        return (
          !deprecationStage ||
          deprecationStage !== DeprecationStage.SUNSET ||
          showDeprecatedSunsetItems
        );
      });
    },
    [getAdditionalCommonModelInfo, showDeprecatedSunsetItems],
  );

  // derived state
  const additionalInfo = getAdditionalCommonModelInfo(commonModel);
  const { deprecationStage, fields } = additionalInfo;
  // The legacy UI reads deprecationMessage directly, but newer entries express deprecation via
  // deprecationDate/deprecationReplacement (and stage-only entries carry neither). Resolve through
  // the same generator the redesign uses so the legacy UI shows the generated "Deprecated on ...
  // Use X instead" copy (or a plain "Deprecated" for stage-only) instead of nothing.
  const deprecationMessage = resolveDeprecationMessage(additionalInfo);

  return {
    filterDeprecatedCommonModels,
    filterDeprecatedFields,
    showDeprecatedSunsetItems,
    deprecationStage,
    deprecationMessage,
    fields,
  };
};

export default useDeprecatedCommonModel;
